import { useId } from 'react';
import type { KeyboardEvent, ReactNode } from 'react';
import { Check } from 'lucide-react';

/**
 * Linearer Stepper für Wizards.
 *
 * - Schritte sind anklickbar, ABER nur Schritte <= aktueller Step
 *   (klassisches "Back / Forward only via CTA"-Pattern).
 * - Tastatur: Pfeil links / rechts wandern zwischen besuchten Schritten.
 * - aria-current="step" für Screen-Reader-Orientierung.
 *
 * Kein Headless-Lib - 80 Zeilen + Tailwind reichen.
 */

export interface StepItem<T extends string> {
  id: T;
  label: string;
  description?: string;
}

interface StepperProps<T extends string> {
  steps: StepItem<T>[];
  active: T;
  /** Liste bereits abgeschlossener Step-IDs (=> großer Haken). */
  completed?: T[];
  onChange: (id: T) => void;
  ariaLabel: string;
  className?: string;
  /** Custom slot rechts (z.B. globaler "Wizard schliessen"-Button). */
  trailing?: ReactNode;
}

export function Stepper<T extends string>({
  steps,
  active,
  completed = [],
  onChange,
  ariaLabel,
  className,
  trailing,
}: StepperProps<T>): JSX.Element {
  const idPrefix = useId();
  const activeIdx = steps.findIndex((s) => s.id === active);

  const handleKey = (event: KeyboardEvent<HTMLOListElement>): void => {
    let next = activeIdx;
    if (event.key === 'ArrowRight') next = Math.min(activeIdx + 1, steps.length - 1);
    else if (event.key === 'ArrowLeft') next = Math.max(activeIdx - 1, 0);
    else return;
    event.preventDefault();
    const candidate = steps[next];
    if (!candidate) return;
    // Nur navigieren wenn schon besucht oder direkt vor active.
    if (next <= activeIdx || completed.includes(candidate.id)) {
      onChange(candidate.id);
    }
  };

  return (
    <div className={['flex items-center gap-3', className ?? ''].join(' ')}>
      <ol
        aria-label={ariaLabel}
        tabIndex={-1}
        onKeyDown={handleKey}
        className="flex flex-1 flex-wrap items-center gap-2"
      >
        {steps.map((step, idx) => {
          const isActive = step.id === active;
          const isDone = completed.includes(step.id) && !isActive;
          const isClickable = idx <= activeIdx || completed.includes(step.id);
          const stepId = `${idPrefix}-${step.id}`;
          return (
            <li
              key={step.id}
              className="flex items-center gap-2"
              aria-current={isActive ? 'step' : undefined}
            >
              <button
                type="button"
                id={stepId}
                onClick={() => isClickable && onChange(step.id)}
                disabled={!isClickable}
                aria-label={`Schritt ${idx + 1} von ${steps.length}: ${step.label}`}
                className={[
                  'flex items-center gap-2 rounded-md border px-3 py-1.5 text-sm transition-colors',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary',
                  isActive
                    ? 'border-primary bg-primary/10 text-primary'
                    : isDone
                      ? 'border-success/40 bg-success/5 text-success'
                      : 'border-border text-muted',
                  !isClickable ? 'cursor-not-allowed opacity-60' : 'hover:bg-surface-2',
                ].join(' ')}
              >
                <span
                  className={[
                    'inline-flex size-5 items-center justify-center rounded-full text-xs font-semibold',
                    isActive
                      ? 'bg-primary text-primary-fg'
                      : isDone
                        ? 'bg-success text-white'
                        : 'bg-surface-2 text-muted',
                  ].join(' ')}
                  aria-hidden="true"
                >
                  {isDone ? <Check className="size-3" /> : idx + 1}
                </span>
                <span className="font-medium">{step.label}</span>
              </button>
              {idx < steps.length - 1 && (
                <span aria-hidden="true" className="h-px w-4 bg-border md:w-8" />
              )}
            </li>
          );
        })}
      </ol>
      {trailing}
    </div>
  );
}
